import React, { Fragment, useCallback, useContext } from 'react'

import { MIXPANEL_EVENTS } from 'lib/mixpanel/events'
import { trackMixpanelEvent } from 'lib/mixpanel/track'
import Router from 'next/router'

import { ProgressBar, Option, StepError } from '../'

import TimezoneContext from 'context/timezoneContext'

import { setMatchMakerQuestion } from 'services/match-maker'

import { Button } from 'components/v2/Button'
import { Column, Row } from 'components/v2/Grid'
import { Text } from 'components/v2/Text'

const TurnSelectionStep = ({ state, dispatch, goToPreviousStep, maxSteps }) => {
	const { timezone } = useContext(TimezoneContext)

	const handlePeriodClick = useCallback(
		(payload) => {
			trackMixpanelEvent(MIXPANEL_EVENTS.match.period_selected_clicked)
			dispatch({
				type: 'set_consultation_turns',
				payload
			})
		},
		[dispatch]
	)

	const submitTurnSelection = useCallback(async () => {
		dispatch({ type: 'set_status', payload: 'loading' })
		try {
			const matchmakingId = localStorage.getItem('idMatchmaking')
			if (matchmakingId) {
				const parsedMatchmakingId = JSON.parse(matchmakingId)
				localStorage.setItem(
					'idMatchmaking',
					JSON.stringify([...parsedMatchmakingId, state.id_session])
				)
			} else {
				localStorage.setItem(
					'idMatchmaking',
					JSON.stringify([state.id_session])
				)
			}
			await setMatchMakerQuestion({
				id_session: state.id_session,
				answers: [
					{
						question: state.questions[12].id,
						answer: String(state.form_values.consultation_turns)
					}
				]
			})
			localStorage.removeItem('@vittude:match-maker')
			Router.push(
				`/encontre-seu-psicologo/resultados?id_session=${state.id_session}&location_preference=online&timezone=${timezone}`
			)
		} catch {
			dispatch({ type: 'set_status', payload: 'error' })
		}
	}, [state.form_values.consultation_turns])

	if (state.status === 'error') {
		return (
			<StepError
				stepImage='/static/images/match-maker/02.png'
				submitStep={submitTurnSelection}
			/>
		)
	}

	return (
		<Fragment>
			<div className='step__illustration lg:hidden'>
				<img
					src='/static/images/match-maker/02.png'
					className='w-full'
					alt=''
				/>
			</div>
			<Column className='max-w-[410px]'>
				<Text className='mb-5 text-left text-lg font-bold text-gray-800 sm:text-xl'>
					Qual o melhor turno para você realizar sua sessão?
				</Text>
				<Text className='mb-[50px] text-sm font-light leading-[25px] text-gray-700'>
					Você pode escolher mais de um turno.
				</Text>
				<Column className='mb-[50px] h-[200px] justify-between'>
					<Row className='max-w-[600px] flex-wrap justify-center'>
						<Option
							selected={state.form_values?.consultation_turns?.includes(
								'morning'
							)}
							onClick={() =>
								handlePeriodClick(
									state.form_values?.consultation_turns?.includes('morning')
										? state.form_values?.consultation_turns.filter(
												(t) => t !== 'morning'
											)
										: [
												...(state.form_values?.consultation_turns ?? []),
												'morning'
											]
								)
							}
						>
							<Column className='items-center justify-center'>
								<Text variant='small' className='font-medium'>
									Manhã
								</Text>
								<Text variant='small' className='text-orange-600'>
									06:00 às 11:59
								</Text>
							</Column>
						</Option>
						<Option
							selected={state.form_values?.consultation_turns?.includes(
								'afternoon'
							)}
							onClick={() =>
								handlePeriodClick(
									state.form_values?.consultation_turns?.includes('afternoon')
										? state.form_values?.consultation_turns.filter(
												(t) => t !== 'afternoon'
											)
										: [
												...(state.form_values?.consultation_turns ?? []),
												'afternoon'
											]
								)
							}
						>
							<Column className='items-center justify-center'>
								<Text variant='small' className='font-medium'>
									Tarde
								</Text>
								<Text variant='small' className='text-orange-600'>
									12:00 às 17:59
								</Text>
							</Column>
						</Option>
						<Option
							selected={state.form_values?.consultation_turns?.includes(
								'night'
							)}
							onClick={() =>
								handlePeriodClick(
									state.form_values?.consultation_turns?.includes('night')
										? state.form_values?.consultation_turns.filter(
												(t) => t !== 'night'
											)
										: [
												...(state.form_values?.consultation_turns ?? []),
												'night'
											]
								)
							}
						>
							<Column className='items-center justify-center'>
								<Text variant='small' className='font-medium'>
									Noite
								</Text>
								<Text variant='small' className='text-orange-600'>
									18:00 às 23:59
								</Text>
							</Column>
						</Option>
						<Option
							selected={state.form_values?.consultation_turns?.includes('dawn')}
							onClick={() =>
								handlePeriodClick(
									state.form_values?.consultation_turns?.includes('dawn')
										? state.form_values?.consultation_turns.filter(
												(t) => t !== 'dawn'
											)
										: [...(state.form_values?.consultation_turns ?? []), 'dawn']
								)
							}
						>
							<Column className='items-center justify-center'>
								<Text variant='small' className='font-medium'>
									Madrugada
								</Text>
								<Text variant='small' className='text-orange-600'>
									00:00 às 05:59
								</Text>
							</Column>
						</Option>
						<Option
							selected={state.form_values?.consultation_turns?.length === 0}
							onClick={() => handlePeriodClick([])}
						>
							<Text variant='small' className='font-medium'>
								Não tenho preferência
							</Text>
						</Option>
					</Row>
				</Column>
				<Column className='items-center justify-center'>
					<Column className='items-center justify-center'>
						<Row className='justify-between'>
							<Button
								className='mr-4 h-[45px] w-40 py-3 text-xs'
								onClick={goToPreviousStep}
								variant='primary-outlined'
							>
								Voltar
							</Button>
							<Button
								className='h-[45px] w-40 py-3 text-xs'
								onClick={submitTurnSelection}
								id='finish-matchmaking'
							>
								Concluir
							</Button>
						</Row>
						{state.current_step > 0 && (
							<ProgressBar maxSteps={maxSteps} visualStep={state.visual_step} />
						)}
					</Column>
				</Column>
			</Column>
		</Fragment>
	)
}

export { TurnSelectionStep }
